import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Skeleton,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  PendingActions as PendingIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';
import AprobarServicioDialog from '../components/AprobarServicioDialog';
import { useSocket } from '../context/SocketContext';
import BackButton from '../components/BackButton';

function AprobarServiciosAdmin() {
  const [servicios, setServicios] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTrabajador, setFiltroTrabajador] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [glosaGlobal, setGlosaGlobal] = useState('');
  const { user, isAuthenticated } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const { socket } = useSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (socket) {
      // Escuchar eventos de aprobación
      socket.on('servicioAprobado', (data) => {
        console.log('Servicio aprobado:', data);
        fetchServicios(); // Refrescar la lista
        showNotification('Servicio aprobado correctamente', 'success');
      });

      socket.on('serviciosAprobadosMasivo', (data) => {
        console.log('Servicios aprobados en masa:', data);
        fetchServicios();
        showNotification(`${data.cantidad} servicios aprobados`, 'success');
      });
    }

    return () => {
      if (socket) {
        socket.off('servicioAprobado');
        socket.off('serviciosAprobadosMasivo');
      }
    };
  }, [socket]);
  
  // ESTE ES EL CAMBIO CLAVE: Solo ejecutar cuando el usuario esté disponible
  useEffect(() => {
    if (user && user.token) {
      fetchServicios();
      fetchTrabajadores();
    }
  }, [user]); // Cambiar la dependencia a user

  useEffect(() => {
    if (user && user.token) { // Verificar que user existe
      if (filtroTrabajador) {
        fetchServiciosPorTrabajador(filtroTrabajador);
      } else {
        fetchServicios();
      }
    }
  }, [filtroTrabajador, user]); // Añadir user como dependencia

  const fetchServicios = async () => {
    // Verificación adicional antes de hacer la petición
    if (!user || !user.token) {
      console.log('Usuario no disponible aún');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/approve-services/pendientes`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setServicios(response.data);
    } catch (error) {
      console.error('Error al obtener los servicios pendientes de aprobación:', error);
      showNotification('Error al cargar los servicios', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiciosPorTrabajador = async (id_trabajador) => {
    // Verificación adicional antes de hacer la petición
    if (!user || !user.token) {
      console.log('Usuario no disponible aún');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/approve-services/por-trabajador/${id_trabajador}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setServicios(response.data);
    } catch (error) {
      console.error('Error al obtener los servicios del trabajador:', error);
      showNotification('Error al cargar los servicios del trabajador', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrabajadores = async () => {
    // Verificación adicional antes de hacer la petición
    if (!user || !user.token) {
      console.log('Usuario no disponible aún');
      return;
    }

    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/approve-services/trabajadores`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setTrabajadores(response.data);
    } catch (error) {
      console.error('Error al obtener los trabajadores:', error);
    }
  };

  const handleAprobarSeleccionados = async () => {
    if (selectedRows.length === 0) {
      showNotification('Selecciona al menos un servicio para aprobar', 'warning');
      return;
    }

    setLoadingApprove(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/approve-services/aprobar-multiples`,
        { 
          ids: selectedRows,
          glosa: glosaGlobal
        },
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      showNotification(`${selectedRows.length} servicios aprobados correctamente`, 'success');
      setSelectedRows([]);
      fetchServicios();
    } catch (error) {
      console.error('Error al aprobar los servicios seleccionados:', error);
      showNotification('Error al aprobar los servicios', 'error');
    } finally {
      setLoadingApprove(false);
    }
  };

  const handleApproveSuccess = () => {
    fetchServicios();
    showNotification('Servicio aprobado correctamente', 'success');
  };

  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Si no hay usuario, mostrar loading
  if (!user || !user.token || loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  const filteredServicios = servicios.filter(servicio => {
    if (!searchTerm) return true;
    const searchTermLower = searchTerm.toLowerCase();
    return (
      String(servicio.id_orden_servicio || '').toLowerCase().includes(searchTermLower) ||
      String(servicio.nombre_cliente || '').toLowerCase().includes(searchTermLower) ||
      String(servicio.apellido_cliente || '').toLowerCase().includes(searchTermLower) ||
      String(servicio.nombre_trabajador || '').toLowerCase().includes(searchTermLower) ||
      String(servicio.apellido_trabajador || '').toLowerCase().includes(searchTermLower)
    );
  });

  const formatCurrency = (value) => {
    const formatted = parseFloat(value || 0).toLocaleString('es-PE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${simboloMoneda} ${formatted}`;
  };

  const columns = [
    {
      field: 'select',
      headerName: 'Seleccionar',
      width: 100,
      renderHeader: () => (
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              onChange={(e) => {
                setSelectAll(e.target.checked);
                if (e.target.checked) {
                  setSelectedRows(filteredServicios.map(s => s.id_asignacion));
                } else {
                  setSelectedRows([]);
                }
              }}
            />
          }
          label=""
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedRows.includes(params.row?.id_asignacion)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, params.row?.id_asignacion]);
            } else {
              setSelectedRows(selectedRows.filter(id => id !== params.row?.id_asignacion));
            }
          }}
        />
      ),
      sortable: false,
      filterable: false
    },
    {
      field: 'id_orden_servicio',
      headerName: '# OS',
      width: 80
    },
    {
      field: 'nombre_cliente',
      headerName: 'Cliente',
      width: 180,
      renderCell: (params) => (
        `${params.row?.nombre_cliente || ''} ${params.row?.apellido_cliente || ''}`
      )
    },
    {
      field: 'nombre_trabajador',
      headerName: 'Trabajador',
      width: 180,
      renderCell: (params) => (
        `${params.row?.nombre_trabajador || ''} ${params.row?.apellido_trabajador || ''}`
      )
    },
    {
      field: 'nombre_local',
      headerName: 'Local',
      width: 150
    },
    {
      field: 'fecha_orden',
      headerName: 'Fecha',
      width: 100,
      renderCell: (params) => {
        if (!params.row?.fecha_orden) return '';
        try {
          return new Date(params.row.fecha_orden).toLocaleDateString();
        } catch (error) {
          return '';
        }
      }
    },
    {
      field: 'precio_servicio',
      headerName: 'Precio',
      width: 100,
      renderCell: (params) => (
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {formatCurrency(params.row?.precio_servicio || 0)}
        </Box>
      )
    },
    {
      field: 'comision',
      headerName: 'Comisión',
      width: 120,
      renderCell: (params) => {
        const precio = parseFloat(params.row?.precio_servicio || 0);
        const pct = parseFloat(params.row?.comision || 0);
        const flat = parseFloat(params.row?.comision_flat || 0);
        const montoComision = flat > 0 ? flat : (precio * pct / 100);
        const label = flat > 0
          ? formatCurrency(flat)
          : `${pct}% (${formatCurrency(montoComision)})`;
        return (
          <Tooltip title={flat > 0 ? `Monto fijo: ${formatCurrency(flat)}` : `${pct}% de ${formatCurrency(precio)}`}>
            <Chip label={label} size="small"
              sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: 11 }} />
          </Tooltip>
        );
      }
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 100,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="info"
            onClick={() => {
              setSelectedServicio(params.row);
              setOpenDialog(true);
            }}
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            color="success"
            onClick={() => {
              setSelectedServicio(params.row);
              setOpenDialog(true);
            }}
          >
            <CheckCircleIcon />
          </IconButton>
        </Box>
      ),
      sortable: false,
      filterable: false
    }
  ];

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>

      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BackButton />
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1A237E">
              Aprobación de Servicios
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Revisa y aprueba los servicios pendientes
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Cards de resumen */}
      {(() => {
        const totalServicios = filteredServicios.length;
        const totalPrecio = filteredServicios.reduce((s, sv) => s + parseFloat(sv.precio_servicio || 0), 0);
        const totalComision = filteredServicios.reduce((s, sv) => {
          const precio = parseFloat(sv.precio_servicio || 0);
          const pct = parseFloat(sv.comision || 0);
          const flat = parseFloat(sv.comision_flat || 0);
          return s + (flat > 0 ? flat : precio * pct / 100);
        }, 0);
        const trabajadoresUnicos = [...new Set(filteredServicios.map(s => s.id_trabajador))].length;

        const StatCard = ({ icon, value, label, color }) => (
          <Grid item xs={6} sm={3}>
            <Paper sx={{
              p: 2, borderRadius: 3, border: `1px solid ${color}25`,
              background: `linear-gradient(135deg, ${color}08, ${color}04)`,
              transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 20px ${color}15` }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `linear-gradient(135deg, ${color}, ${color}CC)`, color: '#fff' }}>
                  {icon}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800} color={color} lineHeight={1.1}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        );

        return (
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <StatCard icon={<AssignmentIcon fontSize="small" />} value={totalServicios} label="Pendientes" color="#1A237E" />
            <StatCard icon={<PersonIcon fontSize="small" />} value={trabajadoresUnicos} label="Trabajadores" color="#7C3AED" />
            <StatCard icon={<MoneyIcon fontSize="small" />} value={formatCurrency(totalPrecio)} label="Monto total" color="#0891B2" />
            <StatCard icon={<PendingIcon fontSize="small" />} value={formatCurrency(totalComision)} label="Comisiones" color="#D97706" />
          </Grid>
        );
      })()}

      {/* Controles y Filtros */}
      <Box sx={{ mb: 2 }}>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Buscar por cliente, trabajador, orden..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#4FC3F7' }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#4FC3F7',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1A237E',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="trabajador-select-label">Filtrar por Trabajador</InputLabel>
              <Select
                labelId="trabajador-select-label"
                value={filtroTrabajador}
                label="Filtrar por Trabajador"
                onChange={(e) => setFiltroTrabajador(e.target.value)}
              >
                <MenuItem value="">Todos los trabajadores</MenuItem>
                {trabajadores.map((trabajador) => (
                  <MenuItem 
                    key={trabajador.id_trabajador} 
                    value={trabajador.id_trabajador}
                  >
                    {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterListIcon />}
              endIcon={<ExpandMoreIcon sx={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
              sx={{
                borderRadius: 2,
                borderColor: '#1A237E',
                color: '#1A237E',
                '&:hover': {
                  borderColor: '#0D47A1',
                  bgcolor: 'rgba(26, 35, 126, 0.04)'
                }
              }}
            >
              Filtros
            </Button>

            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleAprobarSeleccionados}
              disabled={selectedRows.length === 0 || loadingApprove}
              sx={{
                background: selectedRows.length === 0 ? undefined : 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
                borderRadius: 2,
                fontWeight: 'bold',
                '&:hover': {
                  background: selectedRows.length === 0 ? undefined : 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
                }
              }}
            >
              {loadingApprove ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                `Aprobar (${selectedRows.length})`
              )}
            </Button>
          </Grid>
        </Grid>
        
        {showFilters && (
          <Paper sx={{ p: 2, mt: 2, borderRadius: 2, border: '1px solid rgba(26, 35, 126, 0.1)' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E' }}>
              Comentario global para aprobaciones masivas
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              placeholder="Este comentario se aplicará a todos los servicios seleccionados..."
              value={glosaGlobal}
              onChange={(e) => setGlosaGlobal(e.target.value)}
              size="small"
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />

            <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
              Selecciona múltiples servicios para aprobarlos de forma masiva, o utiliza el icono <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> para aprobar individualmente.
            </Alert>
          </Paper>
        )}
      </Box>

      {/* DataGrid */}
      <Paper sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        '& .MuiDataGrid-root': {
          border: 'none',
        },
        '& .MuiDataGrid-columnHeaders': {
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: 'white',
          fontWeight: 'bold',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            color: 'white'
          },
          '& .MuiDataGrid-iconSeparator': {
            color: 'rgba(255, 255, 255, 0.3)'
          },
          '& .MuiCheckbox-root': {
            color: 'white !important'
          }
        },
        '& .MuiDataGrid-row': {
          '&:nth-of-type(even)': {
            backgroundColor: 'rgba(26, 35, 126, 0.02)'
          },
          '&:hover': {
            backgroundColor: 'rgba(79, 195, 247, 0.1)',
          }
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: '1px solid rgba(26, 35, 126, 0.1)',
          backgroundColor: 'rgba(26, 35, 126, 0.02)'
        }
      }}>
        <DataGrid
          rows={filteredServicios}
          columns={columns}
          getRowId={(row) => row.id_asignacion || `${row.id_orden_servicio}-${row.id_trabajador}`}
          pageSize={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          checkboxSelection={false}
          disableSelectionOnClick
          onSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection);
          }}
          selectionModel={selectedRows}
          autoHeight
          sx={{ minHeight: 400 }}
        />
      </Paper>

      <AprobarServicioDialog 
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        servicio={selectedServicio}
        onApproveSuccess={handleApproveSuccess}
      />
      
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AprobarServiciosAdmin;